import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import {
  TrackerToastContainer,
  trackerToast as toast,
} from "../components/tracker/trackerToast";
import {
  TRACKER_BROADCAST_CHANNEL,
  TRACKER_SELECTION_EVENT,
  TRACKER_STORAGE_KEYS,
  parseTrackerTimeParts,
  publishTrackerLoaPrefill,
  readStorageObject,
} from "../utils/trackerBridge";

const TrackerContext = createContext(null);

const DEFAULT_DRAFT = {
  tkt_num: "",
  source: "",
  call_type: "",
  coverage: "",
  caller: "",
  caller_num: "",
  member_name: "",
  member_id: "",
  corp_code: "",
  corp_name: "",
  provider_code: "",
  provider_name: "",
  valid_id: "",
  id_num: "",
  diagnosis: "",
  procedure: "",
  doctor: "",
  loa_no: "",
  trans_no: "",
  form_number: "",
  remarks: "",
  disapproved_amt: "",
  disapproved_remarks: "",
  plan_code: "",
  time_received_hour: "",
  time_received_minute: "",
  time_received_ampm: "",
};

const DEFAULT_BROWSE_FILTERS = {
  search_string: "",
  date_fr: "",
  date_to: "",
  user_id: "",
  source: "",
  status: "",
  ticket_number: "",
  member_name: "",
  coverage: "",
  created_by: "",
  taken_over_by: "",
  page: 1,
  per_page: 10,
  sort_by: "datetime_created",
  sort_direction: "desc",
};

const DEFAULT_BROWSE_PAGINATION = {
  page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
};

const DEFAULT_QUEUE_FILTERS = {
  status: "",
  ticket_number: "",
  member_name: "",
  coverage: "",
  created_by: "",
  taken_over_by: "",
  page: 1,
  per_page: 10,
  sort_by: "datetime_last_update",
  sort_direction: "desc",
};

const DEFAULT_QUEUE_PAGINATION = {
  page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
};

const TRACKER_ATTACHMENT_TOO_LARGE_MESSAGE =
  "Your request data or uploaded files are too large. Please reduce the file size, upload fewer files, or compress the attachments before trying again.";
const TRACKER_REMINDER_INTERVAL_SECONDS = 2 * 60;
const TRACKER_END_OR_EXTEND_SECONDS = 5 * 60;
const TRACKER_REMINDER_REFRESH_MS = 30 * 1000;
const TRACKER_REMINDER_TICK_MS = 1000;
let fallbackReminderDeliveryState = {};

function getAuthHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
    ...extra,
  };
}

function isRequestTooLargeError(error) {
  const status = Number(error?.response?.status || error?.request?.status || 0);
  if (status === 413) {
    return true;
  }

  const responseData = error?.response?.data;
  const responseText =
    typeof responseData === "string"
      ? responseData
      : responseData
      ? JSON.stringify(responseData)
      : "";
  const combinedText = [
    error?.message,
    error?.response?.statusText,
    responseText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    combinedText.includes("413") ||
    combinedText.includes("request entity too large") ||
    combinedText.includes("payload too large") ||
    combinedText.includes("content too large")
  );
}

function getAttachmentUploadErrorMessage(error, fallback) {
  if (
    isRequestTooLargeError(error) ||
    String(error?.message || "").toLowerCase() === "network error"
  ) {
    return TRACKER_ATTACHMENT_TOO_LARGE_MESSAGE;
  }

  return getErrorMessage(error, fallback);
}

function getErrorMessage(error, fallback) {
  const responseData = error?.response?.data;

  if (responseData?.message) {
    const rawMessage = responseData.message;
    let message = "";

    if (typeof rawMessage === "string") {
      message = rawMessage.trim();
    } else if (Array.isArray(rawMessage)) {
      message = rawMessage.map((item) => String(item)).join(" ").trim();
    } else if (typeof rawMessage === "object") {
      message = Object.values(rawMessage)
        .flat()
        .map((item) => String(item))
        .join(" ")
        .trim();
    }

    const description =
      typeof responseData.description === "string" ? responseData.description.trim() : "";
    const requiredScope =
      typeof responseData.required_scope === "string"
        ? responseData.required_scope.trim()
        : "";

    const parts = [];
    if (message) parts.push(message);
    if (description && description !== message) parts.push(description);
    if (requiredScope) parts.push(`Required scope: ${requiredScope}`);

    if (parts.length > 0) {
      return parts.join(" | ");
    }
  }

  return error?.message || fallback;
}

function getReminderDeliveryState() {
  const state = readStorageObject(TRACKER_STORAGE_KEYS.reminderDelivery);
  if (state && typeof state === "object" && !Array.isArray(state)) {
    fallbackReminderDeliveryState = state;
  }

  return { ...fallbackReminderDeliveryState };
}

function saveReminderDeliveryState(state) {
  fallbackReminderDeliveryState = { ...state };

  try {
    if (Object.keys(state).length === 0) {
      localStorage.removeItem(TRACKER_STORAGE_KEYS.reminderDelivery);
      return;
    }

    localStorage.setItem(TRACKER_STORAGE_KEYS.reminderDelivery, JSON.stringify(state));
  } catch {
    // Keep the in-memory delivery state when browser storage is unavailable.
  }
}

function buildDraftFromTicket(ticket) {
  if (!ticket) {
    return { ...DEFAULT_DRAFT };
  }

  const timeParts = parseTrackerTimeParts(ticket.time_received);

  return {
    ...DEFAULT_DRAFT,
    tkt_num: ticket.tkt_num || "",
    source: ticket.source || "",
    call_type: ticket.call_type || "",
    coverage: ticket.coverage || "",
    caller: ticket.caller || "",
    caller_num: ticket.caller_num || "",
    member_name: ticket.member_name || "",
    member_id: ticket.member_id || "",
    corp_code: ticket.corp_code || "",
    corp_name: ticket.corp_name || "",
    provider_code: ticket.provider_code || "",
    provider_name: ticket.provider_name || "",
    valid_id: ticket.valid_id || "",
    id_num: ticket.id_num || "",
    diagnosis: ticket.diagnosis || "",
    procedure: ticket.procedure || "",
    doctor: ticket.doctor || "",
    loa_no: ticket.loa_no || "",
    trans_no: ticket.trans_no || "",
    form_number: ticket.form_number || "",
    remarks: ticket.remarks || "",
    disapproved_amt:
      ticket.disapproved_amt === null || ticket.disapproved_amt === undefined
        ? ""
        : String(ticket.disapproved_amt),
    disapproved_remarks: ticket.disapproved_remarks || "",
    plan_code: ticket.plan_code || "",
    time_received_hour: timeParts.hour,
    time_received_minute: timeParts.minute,
    time_received_ampm: timeParts.ampm,
  };
}

function buildPayloadFromDraft(draft) {
  return {
    tkt_num: draft.tkt_num || null,
    source: draft.source || null,
    call_type: draft.call_type || null,
    coverage: draft.coverage || null,
    caller: draft.caller || null,
    caller_num: draft.caller_num || null,
    member_name: draft.member_name || null,
    member_id: draft.member_id || null,
    corp_code: draft.corp_code || null,
    provider_code: draft.provider_code || null,
    valid_id: draft.valid_id || null,
    id_num: draft.id_num || null,
    diagnosis: draft.diagnosis || null,
    procedure: draft.procedure || null,
    doctor: draft.doctor || null,
    loa_no: draft.loa_no || null,
    trans_no: draft.trans_no || null,
    form_number: draft.form_number || null,
    remarks: draft.remarks || null,
    disapproved_amt: draft.disapproved_amt || null,
    disapproved_remarks: draft.disapproved_remarks || null,
    plan_code: draft.plan_code || null,
    time_received_hour: draft.time_received_hour || null,
    time_received_minute: draft.time_received_minute || null,
    time_received_ampm: draft.time_received_ampm || null,
  };
}

function hasIncompleteTimeReceived(draft) {
  const timeParts = [
    draft.time_received_hour,
    draft.time_received_minute,
    draft.time_received_ampm,
  ].map((value) => String(value || "").trim());
  const filledCount = timeParts.filter(Boolean).length;

  return filledCount > 0 && filledCount < timeParts.length;
}

function normalizeQueueTicketDraft(ticket) {
  return buildDraftFromTicket(ticket);
}

function normalizeTrackerText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function buildMemberDisplayName(payload) {
  if (!payload) return "";

  const firstName = normalizeTrackerText(payload.first_name || payload.firstName);
  const middleName = normalizeTrackerText(payload.middle_name || payload.middleName);
  const lastName = normalizeTrackerText(payload.last_name || payload.lastName);
  const fullName =
    lastName && firstName
      ? `${lastName}, ${firstName}${middleName ? ` ${middleName}` : ""}`
      : [firstName, middleName, lastName].filter(Boolean).join(" ");
  const memberId = normalizeTrackerText(payload.member_id || payload.id);
  const memberName = normalizeTrackerText(payload.member_name || payload.text || payload.name);

  return fullName || (memberName && memberName !== memberId ? memberName : memberId);
}

function mapMemberToTrackerDraft(payload) {
  if (!payload) return null;

  return {
    member_name: buildMemberDisplayName(payload),
    member_id: payload.member_id || payload.id || "",
    corp_code: payload.corp_code || "",
    corp_name:
      payload.corp_name ||
      payload.actual_corp_name ||
      payload.corporate_name ||
      "",
    plan_code: payload.plan_code || "",
  };
}

function mapProviderToTrackerDraft(payload) {
  if (!payload) return null;

  return {
    provider_code: payload.provider_code || payload.id || "",
    provider_name: payload.provider_name || payload.text || "",
  };
}

function parseContentDispositionFileName(headerValue) {
  if (!headerValue) return null;

  const match = headerValue.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!match) return null;

  const encoded = match[1] || match[2];
  try {
    return decodeURIComponent(encoded);
  } catch (error) {
    return encoded;
  }
}

function triggerBlobDownload(blob, fileName) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

export function TrackerProvider({ children }) {
  const [currentTicket, setCurrentTicket] = useState(null);
  const [draft, setDraft] = useState({ ...DEFAULT_DRAFT });
  const [attachments, setAttachments] = useState([]);
  const [queueTickets, setQueueTickets] = useState([]);
  const [queueFilters, setQueueFilters] = useState({ ...DEFAULT_QUEUE_FILTERS });
  const [queuePagination, setQueuePagination] = useState({
    ...DEFAULT_QUEUE_PAGINATION,
  });
  const [queueQueryDebug, setQueueQueryDebug] = useState(null);
  const [browseRows, setBrowseRows] = useState([]);
  const [browseFilters, setBrowseFilters] = useState({ ...DEFAULT_BROWSE_FILTERS });
  const [browsePagination, setBrowsePagination] = useState({
    ...DEFAULT_BROWSE_PAGINATION,
  });
  const [browseQueryDebug, setBrowseQueryDebug] = useState(null);

  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [activeTicketWarning, setActiveTicketWarning] = useState(null);

  const tabIdRef = useRef(`tracker-tab-${Math.random().toString(36).slice(2)}`);
  const channelRef = useRef(null);
  const processedEventsRef = useRef({
    member: 0,
    provider: 0,
    loa: 0,
  });
  const skipNextDraftBroadcastRef = useRef(false);
  const currentTicketRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveInProgressRef = useRef(false);
  const autoSavedPayloadRef = useRef("");
  const autoSaveTicketRef = useRef("");
  const queueFiltersRef = useRef({ ...DEFAULT_QUEUE_FILTERS });
  const browseFiltersRef = useRef({ ...DEFAULT_BROWSE_FILTERS });
  const reminderTicketsRef = useRef([]);

  const broadcast = useCallback((payload) => {
    if (!channelRef.current) return;
    channelRef.current.postMessage({
      ...payload,
      origin: tabIdRef.current,
      at: Date.now(),
    });
  }, []);

  const hydrateFromTicketResponse = useCallback((ticketPayload, preserveDraft = true) => {
    if (!ticketPayload?.ticket) {
      setCurrentTicket(null);
      setDraft({ ...DEFAULT_DRAFT });
      setAttachments([]);
      return;
    }

    const backendDraft = buildDraftFromTicket(ticketPayload.ticket);
    let mergedDraft = backendDraft;

    if (preserveDraft) {
      const savedDraftState = readStorageObject(TRACKER_STORAGE_KEYS.draft);
      if (
        savedDraftState?.tkt_num &&
        savedDraftState.tkt_num === ticketPayload.ticket.tkt_num &&
        savedDraftState?.draft
      ) {
        mergedDraft = {
          ...backendDraft,
          ...savedDraftState.draft,
          tkt_num: backendDraft.tkt_num,
        };
      }
    }

    setCurrentTicket(ticketPayload.ticket);
    setDraft(mergedDraft);
    setAttachments(ticketPayload.attachments || []);
  }, []);

  const loadCurrentTicket = useCallback(
    async ({ silent = false, preserveDraft = true } = {}) => {
      if (!silent) setLoadingCurrent(true);
      try {
        const response = await axios.get(`${BASE_URL}/tracker/current`, {
          headers: getAuthHeaders(),
        });

        hydrateFromTicketResponse(response.data?.data || null, preserveDraft);
        return response.data?.data?.ticket || null;
      } catch (error) {
        if (!silent) {
          toast.error(getErrorMessage(error, "Failed to load current tracker ticket."));
        }
        return null;
      } finally {
        if (!silent) setLoadingCurrent(false);
      }
    },
    [hydrateFromTicketResponse]
  );

  const fetchQueueTickets = useCallback(async ({ silent = false, filters = null } = {}) => {
    const mergedFilters = {
      ...queueFiltersRef.current,
      ...(filters || {}),
    };

    if (filters) {
      queueFiltersRef.current = mergedFilters;
      setQueueFilters(mergedFilters);
    }

    if (!silent) setLoadingQueue(true);

    try {
      const response = await axios.get(`${BASE_URL}/tracker/queue`, {
        headers: getAuthHeaders(),
        params: {
          ...mergedFilters,
          debug: 1,
        },
      });

      const rows = response.data?.data || [];
      const pagination = response.data?.pagination || {
        ...DEFAULT_QUEUE_PAGINATION,
        page: mergedFilters.page || 1,
        per_page: mergedFilters.per_page || DEFAULT_QUEUE_FILTERS.per_page,
      };

      setQueueTickets(rows);
      setQueuePagination(pagination);
      setQueueQueryDebug(response.data?.query_debug || null);
      return rows;
    } catch (error) {
      setQueueQueryDebug(null);
      if (!silent) {
        toast.error(getErrorMessage(error, "Failed to load queue tickets."));
      }
      return [];
    } finally {
      if (!silent) setLoadingQueue(false);
    }
  }, []);

  const loadReminderTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/tracker/reminders`, {
        headers: getAuthHeaders(),
      });
      const syncedAt = Date.now();
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];

      reminderTicketsRef.current = rows
        .filter((ticket) => ticket?.tkt_num)
        .map((ticket) => ({
          ...ticket,
          reminder_synced_at: syncedAt,
        }));

      const activeTicketNumbers = new Set(
        reminderTicketsRef.current.map((ticket) => String(ticket.tkt_num))
      );
      const deliveryState = getReminderDeliveryState();
      const activeDeliveryState = Object.fromEntries(
        Object.entries(deliveryState).filter(([ticketNumber]) =>
          activeTicketNumbers.has(String(ticketNumber))
        )
      );

      if (Object.keys(activeDeliveryState).length !== Object.keys(deliveryState).length) {
        saveReminderDeliveryState(activeDeliveryState);
      }

      return reminderTicketsRef.current;
    } catch (error) {
      if ([401, 403].includes(Number(error?.response?.status || 0))) {
        reminderTicketsRef.current = [];
      }
      return reminderTicketsRef.current;
    }
  }, []);

  const processTrackerReminders = useCallback(() => {
    const now = Date.now();
    const deliveryState = getReminderDeliveryState();
    let deliveryStateChanged = false;

    reminderTicketsRef.current.forEach((ticket) => {
      const ticketNumber = String(ticket?.tkt_num || "").trim();
      if (!ticketNumber) return;

      const elapsedAtSync = Math.max(0, Number(ticket?.elapsed_seconds || 0));
      const secondsSinceSync = Math.max(
        0,
        Math.floor((now - Number(ticket?.reminder_synced_at || now)) / 1000)
      );
      const elapsedSeconds = elapsedAtSync + secondsSinceSync;
      const status = String(ticket?.status || "open").toLowerCase();
      const currentDelivery = deliveryState[ticketNumber] || {};
      const currentTwoMinuteBucket = Math.floor(
        elapsedSeconds / TRACKER_REMINDER_INTERVAL_SECONDS
      );
      const lastTwoMinuteBucket = Number(currentDelivery.last_two_minute_bucket || 0);
      const nextDelivery = { ...currentDelivery };
      let ticketDeliveryChanged = false;

      if (
        currentTwoMinuteBucket >= 1 &&
        currentTwoMinuteBucket > lastTwoMinuteBucket
      ) {
        // Keep one visible recurring reminder per ticket; focus-loss pause can keep old toasts active.
        toast.info(
          `Ticket #${ticketNumber} is still ${status === "queued" ? "queued" : "open"}.`,
          {
            toastId: `tracker-two-minute-${ticketNumber}`,
            autoClose: 10000,
          }
        );
        nextDelivery.last_two_minute_bucket = currentTwoMinuteBucket;
        ticketDeliveryChanged = true;
        deliveryStateChanged = true;
      }

      if (
        elapsedSeconds >= TRACKER_END_OR_EXTEND_SECONDS &&
        !currentDelivery.five_minute_advisory_shown
      ) {
        toast.warning(
          `Ticket #${ticketNumber} has reached 5 minutes. Please end or finish the transaction, or ask the caller if they are willing to extend.`,
          {
            toastId: `tracker-five-minute-${ticketNumber}`,
            autoClose: 15000,
          }
        );
        nextDelivery.five_minute_advisory_shown = true;
        ticketDeliveryChanged = true;
        deliveryStateChanged = true;
      }

      if (ticketDeliveryChanged) {
        deliveryState[ticketNumber] = nextDelivery;
      }
    });

    if (deliveryStateChanged) {
      saveReminderDeliveryState(deliveryState);
    }
  }, []);

  const saveCurrentTicket = useCallback(
    async ({
      silent = false,
      statusOverride = null,
      broadcastRefresh = true,
      trackSavingState = true,
      preserveDraftOnResponse = false,
    } = {}) => {
      if (!draft.tkt_num) {
        if (!silent) {
          toast.info("No active ticket to save.");
        }
        return null;
      }

      if (trackSavingState) {
        setSavingDraft(true);
      }

      try {
        const payload = {
          ...buildPayloadFromDraft(draft),
          ...(statusOverride ? { status: statusOverride } : {}),
        };

        const response = await axios.post(`${BASE_URL}/tracker/save`, payload, {
          headers: getAuthHeaders(),
        });

        hydrateFromTicketResponse(response.data?.data || null, preserveDraftOnResponse);

        if (!silent) {
          toast.success("Tracker ticket saved.");
        }

        if (broadcastRefresh) {
          broadcast({ type: "refresh" });
        }

        return response.data?.data?.ticket || null;
      } catch (error) {
        const message = getErrorMessage(error, "Failed to save tracker ticket.");
        if (!silent) {
          toast.error(message);
        }
        throw new Error(message);
      } finally {
        if (trackSavingState) {
          setSavingDraft(false);
        }
      }
    },
    [broadcast, draft, hydrateFromTicketResponse]
  );

  const createNewTicket = useCallback(
    async ({ activeAction = null, silent = false } = {}) => {
      try {
        const response = await axios.post(
          `${BASE_URL}/tracker/new`,
          activeAction ? { active_action: activeAction } : {},
          { headers: getAuthHeaders() }
        );

        hydrateFromTicketResponse(response.data?.data || null, false);
        setActiveTicketWarning(null);

        await fetchQueueTickets({ silent: true });

        if (!silent) {
          toast.success("New ticket is ready.");
        }

        broadcast({ type: "refresh" });

        return {
          requiresAction: false,
          ticket: response.data?.data?.ticket || null,
        };
      } catch (error) {
        if (error?.response?.status === 409) {
          const activeTicket = error.response?.data?.active_ticket || null;
          setActiveTicketWarning(activeTicket);
          if (!silent) {
            toast.info(
              activeTicket?.tkt_num
                ? `Ticket #${activeTicket.tkt_num} is still active. Choose what to do next.`
                : "An active ticket already exists. Choose what to do next."
            );
          }
          return {
            requiresAction: true,
            activeTicket,
          };
        }

        const message = getErrorMessage(error, "Failed to create a new tracker ticket.");
        if (!silent) {
          toast.error(message);
        }
        const trackerError = new Error(message);
        trackerError.toastShown = !silent;
        throw trackerError;
      }
    },
    [broadcast, fetchQueueTickets, hydrateFromTicketResponse]
  );

  const queueCurrentTicket = useCallback(async () => {
    if (!draft.tkt_num) {
      toast.info("No active ticket to queue.");
      return null;
    }

    try {
      await saveCurrentTicket({ silent: true });

      await axios.post(
        `${BASE_URL}/tracker/queue`,
        { tkt_num: draft.tkt_num },
        { headers: getAuthHeaders() }
      );

      await Promise.all([
        loadCurrentTicket({ silent: true, preserveDraft: false }),
        fetchQueueTickets({ silent: true }),
        loadReminderTickets(),
      ]);

      toast.success(`Ticket #${draft.tkt_num} moved to queue.`);
      broadcast({ type: "refresh" });
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to queue current ticket."));
      throw error;
    }
  }, [
    broadcast,
    draft.tkt_num,
    fetchQueueTickets,
    loadCurrentTicket,
    loadReminderTickets,
    saveCurrentTicket,
  ]);

  const switchToQueueTicket = useCallback(
    async (tktNum) => {
      if (!tktNum) {
        toast.error("Queue ticket number is required.");
        return null;
      }

      try {
        if (draft.tkt_num && draft.tkt_num !== tktNum) {
          await saveCurrentTicket({ silent: true });
        }

        const response = await axios.post(
          `${BASE_URL}/tracker/queue/switch`,
          { tkt_num: tktNum },
          { headers: getAuthHeaders() }
        );

        hydrateFromTicketResponse(response.data?.data || null, false);

        await fetchQueueTickets({ silent: true });

        const autoQueuedTicket = response.data?.data?.auto_queued_ticket;
        if (autoQueuedTicket) {
          toast.success(
            `Loaded #${tktNum}. Previous active ticket #${autoQueuedTicket} was auto-queued.`
          );
        } else {
          toast.success(`Loaded queued ticket #${tktNum}.`);
        }

        broadcast({ type: "refresh" });
        return response.data?.data?.ticket || null;
      } catch (error) {
        const message = getErrorMessage(error, "Unable to switch queued ticket.");
        toast.error(message);
        throw new Error(message);
      }
    },
    [broadcast, draft.tkt_num, fetchQueueTickets, hydrateFromTicketResponse, saveCurrentTicket]
  );

  const takeOverTicket = useCallback(
    async (tktNum, { remarks = null } = {}) => {
      if (!tktNum) {
        toast.error("Ticket number is required for takeover.");
        return null;
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/tracker/takeover`,
          {
            tkt_num: tktNum,
            ...(remarks ? { remarks } : {}),
          },
          { headers: getAuthHeaders() }
        );

        hydrateFromTicketResponse(response.data?.data || null, false);

        await fetchQueueTickets({ silent: true });

        const autoQueuedTicket = response.data?.data?.auto_queued_ticket;
        if (autoQueuedTicket) {
          toast.success(
            `Takeover successful. #${tktNum} is now active. Previous active #${autoQueuedTicket} was auto-queued.`
          );
        } else {
          toast.success(`Takeover successful. #${tktNum} is now active.`);
        }

        broadcast({ type: "refresh" });
        return response.data?.data?.ticket || null;
      } catch (error) {
        const message = getErrorMessage(error, "Unable to take over tracker ticket.");
        toast.error(message);
        throw new Error(message);
      }
    },
    [broadcast, fetchQueueTickets, hydrateFromTicketResponse]
  );

  const editCreatorTicket = useCallback(
    async (tktNum, { silentToast = false } = {}) => {
      if (!tktNum) {
        const message = "Ticket number is required for edit.";
        if (!silentToast) {
          toast.error(message);
        }
        throw new Error(message);
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/tracker/creator-edit`,
          { tkt_num: tktNum },
          { headers: getAuthHeaders() }
        );

        hydrateFromTicketResponse(response.data?.data || null, false);

        await fetchQueueTickets({ silent: true });

        const autoQueuedTicket = response.data?.data?.auto_queued_ticket;
        if (!silentToast) {
          if (autoQueuedTicket) {
            toast.success(
              `Ticket #${tktNum} loaded for editing. Previous active #${autoQueuedTicket} was auto-queued.`
            );
          } else {
            toast.success(`Ticket #${tktNum} loaded for editing.`);
          }
        }

        broadcast({ type: "refresh" });
        return response.data?.data?.ticket || null;
      } catch (error) {
        const message = getErrorMessage(error, "Unable to load tracker ticket for editing.");
        if (!silentToast) {
          toast.error(message);
        }

        const wrappedError = new Error(message);
        wrappedError.response = error?.response;
        throw wrappedError;
      }
    },
    [broadcast, fetchQueueTickets, hydrateFromTicketResponse]
  );

  const closeCurrentTicket = useCallback(async () => {
    if (!draft.tkt_num) {
      toast.info("No active ticket to close.");
      return null;
    }

    try {
      const payload = buildPayloadFromDraft(draft);
      await axios.post(`${BASE_URL}/tracker/close`, payload, {
        headers: getAuthHeaders(),
      });

      await Promise.all([
        loadCurrentTicket({ silent: true, preserveDraft: false }),
        fetchQueueTickets({ silent: true }),
        loadReminderTickets(),
      ]);

      toast.success(`Ticket #${draft.tkt_num} closed.`);
      broadcast({ type: "refresh" });
      return true;
    } catch (error) {
      const message = getErrorMessage(error, "Unable to close ticket.");
      toast.error(message);
      throw new Error(message);
    }
  }, [broadcast, draft, fetchQueueTickets, loadCurrentTicket, loadReminderTickets]);

  const fetchTicketByNumber = useCallback(async (tktNum, { forBrowse = false } = {}) => {
    if (!tktNum) {
      throw new Error("Ticket number is required.");
    }

    try {
      const response = await axios.get(`${BASE_URL}/tracker/ticket`, {
        headers: getAuthHeaders(),
        params: {
          tkt_num: tktNum,
          ...(forBrowse ? { for_browse: 1 } : {}),
        },
      });

      return response.data?.data || null;
    } catch (error) {
      const message = getErrorMessage(error, "Unable to load ticket details.");
      toast.error(message);
      throw new Error(message);
    }
  }, []);

  const fetchTakeoverHistory = useCallback(async (tktNum, { debug = true } = {}) => {
    if (!tktNum) {
      throw new Error("Ticket number is required.");
    }

    try {
      const response = await axios.get(`${BASE_URL}/tracker/takeover-history`, {
        headers: getAuthHeaders(),
        params: {
          tkt_num: tktNum,
          ...(debug ? { debug: 1 } : {}),
        },
      });

      return {
        rows: response.data?.data || [],
        queryDebug: response.data?.query_debug || null,
      };
    } catch (error) {
      const message = getErrorMessage(error, "Unable to load takeover history.");
      toast.error(message);
      throw new Error(message);
    }
  }, []);

  const saveQueueTicketDraft = useCallback(async (
    queueDraft,
    status = "queued",
    { silentToast = false } = {}
  ) => {
    const payload = {
      ...buildPayloadFromDraft(queueDraft),
      status,
    };

    try {
      const response = await axios.post(`${BASE_URL}/tracker/save`, payload, {
        headers: getAuthHeaders(),
      });

      await Promise.all([
        fetchQueueTickets({ silent: true }),
        loadReminderTickets(),
      ]);
      broadcast({ type: "refresh" });
      if (!silentToast) {
        toast.success("Queue ticket saved.");
      }

      return response.data?.data || null;
    } catch (error) {
      const message = getErrorMessage(error, "Unable to save queued ticket.");
      if (!silentToast) {
        toast.error(message);
      }
      throw new Error(message);
    }
  }, [broadcast, fetchQueueTickets, loadReminderTickets]);

  const closeQueueTicketDraft = useCallback(async (queueDraft, { silentToast = false } = {}) => {
    const payload = {
      ...buildPayloadFromDraft(queueDraft),
    };

    try {
      const response = await axios.post(`${BASE_URL}/tracker/close`, payload, {
        headers: getAuthHeaders(),
      });

      await Promise.all([
        fetchQueueTickets({ silent: true }),
        loadReminderTickets(),
      ]);
      broadcast({ type: "refresh" });
      if (!silentToast) {
        toast.success(`Queue ticket #${queueDraft?.tkt_num || "-"} closed.`);
      }

      return response.data?.data || null;
    } catch (error) {
      const message = getErrorMessage(error, "Unable to close queued ticket.");
      if (!silentToast) {
        toast.error(message);
      }
      throw new Error(message);
    }
  }, [broadcast, fetchQueueTickets, loadReminderTickets]);

  const reloadAttachments = useCallback(async (ticketNumber = null) => {
    const tktNum = ticketNumber || draft.tkt_num;
    if (!tktNum) return [];

    const response = await axios.get(`${BASE_URL}/tracker/attachments`, {
      headers: getAuthHeaders(),
      params: { tkt_num: tktNum },
    });

    const items = response.data?.data || [];

    if (currentTicketRef.current?.tkt_num === tktNum) {
      setAttachments(items);
    }

    return items;
  }, [draft.tkt_num]);

  const uploadTicketAttachments = useCallback(
    async (files, ticketNumber = null) => {
      const tktNum = ticketNumber || draft.tkt_num;
      if (!tktNum) {
        throw new Error("Create or load a ticket before uploading files.");
      }

      const fileArray = Array.from(files || []);
      if (fileArray.length === 0) {
        return;
      }

      setUploadingAttachments(true);

      try {
        const formData = new FormData();
        formData.append("tkt_num", tktNum);
        fileArray.forEach((file) => formData.append("files[]", file));

        await axios.post(`${BASE_URL}/tracker/attachments`, formData, {
          headers: getAuthHeaders({
            "Content-Type": "multipart/form-data",
          }),
        });

        await reloadAttachments(tktNum);

        toast.success(`${fileArray.length} attachment(s) uploaded.`);
        broadcast({ type: "refresh" });
      } catch (error) {
        const message = getAttachmentUploadErrorMessage(
          error,
          "Failed to upload tracker attachment(s)."
        );
        toast.error(message);
        throw new Error(message);
      } finally {
        setUploadingAttachments(false);
      }
    },
    [broadcast, draft.tkt_num, reloadAttachments]
  );

  const deleteTicketAttachment = useCallback(
    async (uploadId, ticketNumber = null) => {
      if (!uploadId) {
        return;
      }

      const tktNum = ticketNumber || draft.tkt_num;
      try {
        await axios.delete(`${BASE_URL}/tracker/attachments/${uploadId}`, {
          headers: getAuthHeaders(),
        });

        await reloadAttachments(tktNum);
        toast.success("Attachment deleted.");
        broadcast({ type: "refresh" });
      } catch (error) {
        const message = getErrorMessage(error, "Unable to delete attachment.");
        toast.error(message);
        throw new Error(message);
      }
    },
    [broadcast, draft.tkt_num, reloadAttachments]
  );

  const refreshTicketAttachmentUrl = useCallback(async (uploadId, { silentToast = false } = {}) => {
    if (!uploadId) return null;
    try {
      const response = await axios.post(
        `${BASE_URL}/tracker/attachments/refresh-url`,
        { upload_id: uploadId },
        { headers: getAuthHeaders() }
      );

      const refreshed = response.data?.data;
      if (!refreshed) {
        if (!silentToast) {
          toast.info("No attachment URL update was returned.");
        }
        return null;
      }

      setAttachments((prev) =>
        prev.map((item) =>
          item.upload_id === uploadId
            ? {
                ...item,
                ...refreshed,
              }
            : item
        )
      );

      if (!silentToast) {
        toast.success("Attachment URL refreshed.");
      }
      return refreshed;
    } catch (error) {
      const message = getErrorMessage(error, "Unable to refresh attachment URL.");
      if (!silentToast) {
        toast.error(message);
      }
      throw new Error(message);
    }
  }, []);

  const fetchBrowseTickets = useCallback(async (filters = {}) => {
    const mergedFilters = {
      ...browseFiltersRef.current,
      ...filters,
    };

    browseFiltersRef.current = mergedFilters;
    setLoadingBrowse(true);
    setBrowseFilters(mergedFilters);

    try {
      const response = await axios.get(`${BASE_URL}/tracker/browse`, {
        headers: getAuthHeaders(),
        params: {
          ...mergedFilters,
          debug: 1,
        },
      });

      setBrowseRows(response.data?.data || []);
      setBrowsePagination(response.data?.pagination || { ...DEFAULT_BROWSE_PAGINATION });
      setBrowseQueryDebug(response.data?.query_debug || null);
    } catch (error) {
      setBrowseQueryDebug(null);
      toast.error(getErrorMessage(error, "Failed to load browse tickets."));
    } finally {
      setLoadingBrowse(false);
    }
  }, []);

  const exportBrowseTickets = useCallback(async (filters = {}) => {
    const mergedFilters = {
      ...browseFiltersRef.current,
      ...filters,
    };
    try {
      const response = await axios.get(`${BASE_URL}/tracker/browse/export`, {
        headers: getAuthHeaders(),
        params: mergedFilters,
        responseType: "blob",
      });

      const fileName =
        parseContentDispositionFileName(response.headers?.["content-disposition"]) ||
        `tracker_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.csv`;

      triggerBlobDownload(response.data, fileName);
      toast.success("Tracker CSV downloaded.");
    } catch (error) {
      const message = getErrorMessage(error, "Unable to export tracker CSV.");
      toast.error(message);
      throw new Error(message);
    }
  }, []);

  const setDraftField = useCallback((field, value) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const setDraftFields = useCallback((updates) => {
    setDraft((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const prepareGenerateLoaPrefill = useCallback((returnTo = "/tracker") => {
    const payload = {
      tkt_num: draft.tkt_num,
      returnTo,
      member: draft.member_id
        ? {
            id: draft.member_id,
            text: draft.member_name || draft.member_id,
          }
        : null,
      provider: draft.provider_code
        ? {
            id: draft.provider_code,
            text: draft.provider_name || draft.provider_code,
          }
        : null,
      coverage: draft.coverage
        ? {
            id: draft.coverage,
            text: draft.coverage,
          }
        : null,
    };

    publishTrackerLoaPrefill(payload);
    return payload;
  }, [draft]);

  const applyMemberSelection = useCallback((payload, { silentToast = false } = {}) => {
    const mapped = mapMemberToTrackerDraft(payload);
    if (!mapped) return;

    setDraft((prev) => ({
      ...prev,
      ...mapped,
    }));

    if (!silentToast) {
      toast.success("Member selected for Tracker.");
    }
  }, []);

  const applyProviderSelection = useCallback((payload, { silentToast = false } = {}) => {
    const mapped = mapProviderToTrackerDraft(payload);
    if (!mapped) return;

    setDraft((prev) => ({
      ...prev,
      ...mapped,
    }));

    if (!silentToast) {
      toast.success("Provider selected for Tracker.");
    }
  }, []);

  const applyLoaResult = useCallback((payload, { silentToast = false } = {}) => {
    if (!payload) return;

    setDraft((prev) => {
      const targetTicket = payload.tkt_num || payload.ticket_no || null;
      if (targetTicket && prev.tkt_num && String(targetTicket) !== String(prev.tkt_num)) {
        return prev;
      }

      return {
        ...prev,
        trans_no: payload.trans_no || payload.transNo || prev.trans_no,
        loa_no: payload.loa_no || payload.loaNo || prev.loa_no,
        form_number: payload.form_number || payload.formNo || prev.form_number,
        plan_code: payload.plan_code || payload.planCode || prev.plan_code,
      };
    });

    if (!silentToast) {
      toast.success("TRN/LOA details returned to Tracker.");
    }
  }, []);

  const consumeStorageEnvelope = useCallback(
    (type, envelope) => {
      if (!envelope || !envelope.payload) return;

      const eventAt = Number(envelope.at || 0);
      if (eventAt <= processedEventsRef.current[type]) return;
      processedEventsRef.current[type] = eventAt;

      if (type === "member") {
        applyMemberSelection(envelope.payload, { silentToast: true });
      } else if (type === "provider") {
        applyProviderSelection(envelope.payload, { silentToast: true });
      } else if (type === "loa") {
        applyLoaResult(envelope.payload, { silentToast: true });
      }
    },
    [applyLoaResult, applyMemberSelection, applyProviderSelection]
  );

  const consumeAllSelectionStorage = useCallback(() => {
    consumeStorageEnvelope("member", readStorageObject(TRACKER_STORAGE_KEYS.memberSelection));
    consumeStorageEnvelope("provider", readStorageObject(TRACKER_STORAGE_KEYS.providerSelection));
    consumeStorageEnvelope("loa", readStorageObject(TRACKER_STORAGE_KEYS.loaResult));
  }, [consumeStorageEnvelope]);

  useEffect(() => {
    currentTicketRef.current = currentTicket;
  }, [currentTicket]);

  useEffect(() => {
    queueFiltersRef.current = queueFilters;
  }, [queueFilters]);

  useEffect(() => {
    browseFiltersRef.current = browseFilters;
  }, [browseFilters]);

  useEffect(() => {
    const ticketNumber = currentTicket?.tkt_num || "";
    if (!ticketNumber) {
      autoSaveTicketRef.current = "";
      autoSavedPayloadRef.current = "";
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    if (autoSaveTicketRef.current === ticketNumber) {
      return;
    }

    autoSaveTicketRef.current = ticketNumber;
    autoSavedPayloadRef.current = JSON.stringify(buildPayloadFromDraft(draft));
  }, [currentTicket?.tkt_num, draft]);

  useEffect(() => {
    if (!currentTicket?.tkt_num) {
      return;
    }

    localStorage.setItem(
      TRACKER_STORAGE_KEYS.draft,
      JSON.stringify({
        tkt_num: currentTicket.tkt_num,
        draft,
        at: Date.now(),
      })
    );
  }, [currentTicket?.tkt_num, draft]);

  useEffect(() => {
    if (!currentTicket?.tkt_num) {
      return;
    }

    if (skipNextDraftBroadcastRef.current) {
      skipNextDraftBroadcastRef.current = false;
      return;
    }

    broadcast({
      type: "draft:update",
      tkt_num: currentTicket.tkt_num,
      draft,
    });
  }, [broadcast, currentTicket?.tkt_num, draft]);

  useEffect(() => {
    if (!currentTicket?.tkt_num) {
      return undefined;
    }

    const normalizedStatus = String(currentTicket?.status || "").toLowerCase();
    if (normalizedStatus && normalizedStatus !== "open" && normalizedStatus !== "new") {
      return undefined;
    }

    if (hasIncompleteTimeReceived(draft)) {
      return undefined;
    }

    const payloadHash = JSON.stringify(buildPayloadFromDraft(draft));
    if (payloadHash === autoSavedPayloadRef.current) {
      return undefined;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(async () => {
      if (autoSaveInProgressRef.current) {
        return;
      }

      autoSaveInProgressRef.current = true;

      try {
        await saveCurrentTicket({
          silent: true,
          broadcastRefresh: false,
          trackSavingState: false,
          preserveDraftOnResponse: true,
        });
        autoSavedPayloadRef.current = payloadHash;
      } catch (error) {
        // Keep autosave silent; user-triggered actions still show toasts.
      } finally {
        autoSaveInProgressRef.current = false;
      }
    }, 1500);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [currentTicket?.status, currentTicket?.tkt_num, draft, saveCurrentTicket]);

  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }

    const channel = new BroadcastChannel(TRACKER_BROADCAST_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const data = event?.data || {};
      if (data.origin === tabIdRef.current) return;

      if (data.type === "refresh") {
        loadCurrentTicket({ silent: true });
        fetchQueueTickets({ silent: true });
        loadReminderTickets();
      }

      if (
        data.type === "draft:update" &&
        data.tkt_num &&
        data.tkt_num === currentTicketRef.current?.tkt_num &&
        data.draft
      ) {
        skipNextDraftBroadcastRef.current = true;
        setDraft((prev) => ({
          ...prev,
          ...data.draft,
          tkt_num: prev.tkt_num,
        }));
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [fetchQueueTickets, loadCurrentTicket, loadReminderTickets]);

  useEffect(() => {
    const onStorage = (event) => {
      if (!event.key) return;

      if (event.key === TRACKER_STORAGE_KEYS.memberSelection) {
        consumeStorageEnvelope("member", readStorageObject(TRACKER_STORAGE_KEYS.memberSelection));
      }

      if (event.key === TRACKER_STORAGE_KEYS.providerSelection) {
        consumeStorageEnvelope("provider", readStorageObject(TRACKER_STORAGE_KEYS.providerSelection));
      }

      if (event.key === TRACKER_STORAGE_KEYS.loaResult) {
        consumeStorageEnvelope("loa", readStorageObject(TRACKER_STORAGE_KEYS.loaResult));
      }
    };

    const onCustomSelectionEvent = () => {
      consumeAllSelectionStorage();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(TRACKER_SELECTION_EVENT, onCustomSelectionEvent);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(TRACKER_SELECTION_EVENT, onCustomSelectionEvent);
    };
  }, [consumeAllSelectionStorage, consumeStorageEnvelope]);

  useEffect(() => {
    loadCurrentTicket({ silent: true });
    fetchQueueTickets({ silent: true });
    loadReminderTickets();
    consumeAllSelectionStorage();
  }, [
    consumeAllSelectionStorage,
    fetchQueueTickets,
    loadCurrentTicket,
    loadReminderTickets,
  ]);

  useEffect(() => {
    const refreshTimer = setInterval(loadReminderTickets, TRACKER_REMINDER_REFRESH_MS);
    const reminderTimer = setInterval(processTrackerReminders, TRACKER_REMINDER_TICK_MS);

    return () => {
      clearInterval(refreshTimer);
      clearInterval(reminderTimer);
    };
  }, [loadReminderTickets, processTrackerReminders]);

  const value = useMemo(
    () => ({
      currentTicket,
      draft,
      attachments,
      queueTickets,
      queueFilters,
      queuePagination,
      queueQueryDebug,
      browseRows,
      browseFilters,
      browsePagination,
      browseQueryDebug,
      loadingCurrent,
      loadingQueue,
      loadingBrowse,
      savingDraft,
      uploadingAttachments,
      activeTicketWarning,
      setActiveTicketWarning,
      normalizeQueueTicketDraft,

      loadCurrentTicket,
      fetchQueueTickets,
      fetchTicketByNumber,
      fetchTakeoverHistory,
      saveCurrentTicket,
      createNewTicket,
      queueCurrentTicket,
      switchToQueueTicket,
      takeOverTicket,
      editCreatorTicket,
      closeCurrentTicket,

      saveQueueTicketDraft,
      closeQueueTicketDraft,

      reloadAttachments,
      uploadTicketAttachments,
      deleteTicketAttachment,
      refreshTicketAttachmentUrl,

      fetchBrowseTickets,
      exportBrowseTickets,
      setBrowseFilters,
      setQueueFilters,

      setDraftField,
      setDraftFields,
      prepareGenerateLoaPrefill,

      applyMemberSelection,
      applyProviderSelection,
      applyLoaResult,
    }),
    [
      activeTicketWarning,
      applyLoaResult,
      applyMemberSelection,
      applyProviderSelection,
      attachments,
      browseFilters,
      browseQueryDebug,
      browsePagination,
      browseRows,
      closeCurrentTicket,
      closeQueueTicketDraft,
      createNewTicket,
      currentTicket,
      deleteTicketAttachment,
      draft,
      editCreatorTicket,
      exportBrowseTickets,
      fetchBrowseTickets,
      fetchQueueTickets,
      fetchTicketByNumber,
      fetchTakeoverHistory,
      loadCurrentTicket,
      loadingBrowse,
      loadingCurrent,
      loadingQueue,
      prepareGenerateLoaPrefill,
      queueCurrentTicket,
      queueTickets,
      queueFilters,
      queuePagination,
      queueQueryDebug,
      refreshTicketAttachmentUrl,
      reloadAttachments,
      saveCurrentTicket,
      saveQueueTicketDraft,
      savingDraft,
      setDraftField,
      setDraftFields,
      switchToQueueTicket,
      takeOverTicket,
      uploadingAttachments,
      uploadTicketAttachments,
    ]
  );

  return (
    <TrackerContext.Provider value={value}>
      {children}
      <TrackerToastContainer />
    </TrackerContext.Provider>
  );
}

export function useTracker() {
  const context = useContext(TrackerContext);
  if (!context) {
    throw new Error("useTracker must be used inside TrackerProvider.");
  }
  return context;
}
